import { Container, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import { FilterOptionsState } from "@mui/material/useAutocomplete";
import { useTranslation } from "common/locales";
import { throttle } from "lodash";
import { instanceOfPostcode, Postcode } from "model/geolocation";
import { useCallback, useState } from "react";
import { fetchPostcodes } from "services";
import { useUIStore } from "stores";

type ChangeEventType = React.ChangeEvent<object>;

export default function PostcodeLookupInput() {
  const { t } = useTranslation();

  const [postcode, setPostcode] = useState<Postcode | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [inputError, setInputError] = useState<boolean>(false);
  const isValidPostcode = (postcode: string) =>
    postcode && postcode.trim().length >= 1;

  // Use Zustand for postcode list
  const tmpPostcodeList = useUIStore((state) => state.tmpPostcodeList);
  const selectPostcodeAction = useUIStore((state) => state.selectPostcode);
  const setTmpPostcodeList = useUIStore((state) => state.setTmpPostcodeList);

  const findPostcode = useCallback(
    throttle(async (postcode: string) => {
      const sanitizedPostcode = sanitizePostcode(postcode);
      const postcodes = await fetchPostcodes(sanitizedPostcode);
      setTmpPostcodeList(postcodes);
    }, 200),
    []
  );

  const onInputChange = useCallback(
    async (e: ChangeEventType, newInputValue: string) => {
      // user types something
      setInputValue(newInputValue);
      setInputError(false);
      if (isValidPostcode(newInputValue)) {
        return findPostcode(newInputValue);
      }
    },
    []
  );

  const onChange = useCallback(
    (e: ChangeEventType, newValue: Postcode | string | null) => {
      // user selects item
      if (!newValue) {
        return;
      }

      if (instanceOfPostcode(newValue)) {
        setPostcode(newValue);
        selectPostcodeAction(newValue);
        return;
      }

      // otherwise trying to find suitable postcode for user's text input
      const sanitizedNewValue = sanitizePostcode(newValue);
      const newPostcode = tmpPostcodeList
        .filter((item) => sanitizedNewValue === item.code)
        .pop();

      if (newPostcode) {
        setPostcode(newPostcode);
        selectPostcodeAction(newPostcode);
        return;
      }

      setInputError(true); // selected non-existing postcode
    },
    [tmpPostcodeList, selectPostcodeAction]
  );

  // Custom filter options in order to fuzzy match postcodes
  const filterOptions = (
    options: Postcode[],
    state: FilterOptionsState<Postcode>
  ) =>
    options.filter((option) =>
      option.code.startsWith(sanitizePostcode(state.inputValue))
    );

  return (
    <PostcodeLookupContainer maxWidth={false}>
      <Autocomplete
        color="primary"
        options={tmpPostcodeList}
        getOptionLabel={(option) =>
          instanceOfPostcode(option) ? option.formattedCode : option
        }
        freeSolo
        fullWidth
        filterOptions={filterOptions}
        onInputChange={onInputChange}
        onChange={onChange}
        value={postcode}
        inputValue={inputValue}
        renderInput={(params) => (
          <TextField
            {...params}
            color="primary"
            label={t("map.postcodeInput.helperText")}
            variant="outlined"
            placeholder={t("map.postcodeInput.placeholder")}
            error={inputError}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          />
        )}
      />
    </PostcodeLookupContainer>
  );
}

export const PostcodeLookupContainer = styled(Container)(({ theme }) => ({
  paddingTop: `calc(env(safe-area-inset-top) + ${theme.spacing(1)})`,
  paddingBottom: `calc(env(safe-area-inset-bottom) + ${theme.spacing(1)})`,
  paddingLeft: `calc(env(safe-area-inset-left) + ${theme.spacing(2)})`,
  paddingRight: `calc(env(safe-area-inset-right) + ${theme.spacing(2)})`,
  width: "100%",
  display: "block",
  background: "transparent !important",
  backgroundColor: "transparent !important",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: `calc(env(safe-area-inset-top) + ${theme.spacing(
      1
    )} + 56px + 20px)`,
    backgroundColor: "white",
    zIndex: -1,
  },
  [theme.breakpoints.up("sm")]: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    "&::before": {
      height: `calc(${theme.spacing(1)} + 56px + 20px)`,
    },
  },
}));

function sanitizePostcode(postcode: string): string {
  return (postcode || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
}
